
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableSortLabel,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Dialog,
    Slide,
    Tooltip,
    TextField
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses';
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import {useTheme} from '@mui/material/styles';
import { useNavigate } from "react-router";
import {ListViewModel,
        ListViewColumnModel,
        ActionModel,
        RecordModel,
        RecordJSON,
        RecordFactory,
        MergedRecordModel,
        MergedRecordFactory,
        FieldModel,
        FieldMetadataModel,
        FieldMetadataJSON,
        FieldMetadataFactory} from '../../models/models';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SelectAllIcon from '@mui/icons-material/SelectAll';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {SearchFieldComponent} from "../../components/searchField/SearchField";
import {useSnackbar} from "notistack";
import { formatDate, formatDatetime } from "../../utils/dateFunctions";
import {Link, useParams} from 'react-router-dom';
import {getAppName, getObjectName } from "../../utils/helperRouter";
import Config from "../../config";
import { useAuth } from "../../hooks/useAuth";
import Plot from 'react-plotly.js'


export const DuplicateListComponent: React.FC<{
  data: any,
  action: ActionModel,
  onMerge: () => void
}> = ({
  data,
  action,
  onMerge
}) => {

  const styles = ContainerClasses();

  let {appName, objectName, recordUuid} = useParams();
  const {accessToken, onInvalidToken} = useAuth();

  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const [duplicateRecords, setDuplicateRecords] = useState<RecordModel[]>([]);
  const [fields, setFields] = useState<FieldMetadataModel[]>([]);
  const [mergedRecord, setMergedRecord] = useState<MergedRecordModel>(new MergedRecordFactory().init())
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);


  useEffect(() => {
    setErrorMessage(null)

    if(data){

      if(!data.fields || !Array.isArray(data.fields)){
        setErrorMessage('Error: cannot load duplicate records - field metadata')
        return
      }
      if(!data.records || !Array.isArray(data.records)){
        setErrorMessage('Error: cannot load duplicate records')
        return
      }
      if(!data.merged_record){
        setErrorMessage('Error: cannot load merged record')
        return
      }

      const fieldMetadataFactory = new FieldMetadataFactory()
      const fieldsLoaded = data.fields.map((thisField: FieldMetadataJSON) => fieldMetadataFactory.fromJSON(thisField))
      setFields(fieldsLoaded);

      const recordFactory = new RecordFactory();
      const records = data.records.map((thisRecord: RecordJSON) => recordFactory.fromJSON(thisRecord));
      setDuplicateRecords(records);

      const mergedRecord = new MergedRecordFactory().fromJSON(data.merged_record);
      setMergedRecord(mergedRecord)

    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if(errorMessage){
     enqueueSnackbar(errorMessage, {
          variant: "error",
       });
    }
  }, [errorMessage]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if(isLoading){
      document.body.style.cursor = "wait";
    } else {
      document.body.style.cursor = "default";
    }

  }, [isLoading]);




  const submitMerge = async () => {

    const thisObjectApiName = getObjectName(objectName)
    const mainRecordUuid = mergedRecord['Uuid_sys']
    setIsLoading(true)
    setErrorMessage(null)

    try {
      let url = `${Config.getInstance().getCoordinationServiceURL()}/api/m/${thisObjectApiName}/${mainRecordUuid}`;
      const headers = new Headers();

      headers.append("Authorization", `Bearer ${accessToken}`);
      headers.append("Accept", "application/json");
      headers.append("Content-Type", "application/json");

       const requestBody = {'uuids': duplicateRecords.map((thisRecord) => thisRecord.fields['Uuid_sys'].fieldValue),
                            'merged_record': new MergedRecordFactory().toJSON(mergedRecord)};

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody)
      })

      if(!response.ok) {
        const errorData = await response.json();
        if(!errorData.message){
          setErrorMessage("Error when merging record");
        } else{
          setErrorMessage(errorData.message);
        }
        setIsLoading(false)
        return;
      }

      const data = await response.json();
      if(!data){
        setErrorMessage("Unhandled Error in DuplicateList")
        setIsLoading(false)
        return
      }
      if(!data.main_record_uuid){
        setErrorMessage("Unhandled Error in DuplicateList - main_record_uuid")
        setIsLoading(false)
        return
      }
      setIsLoading(false)

      const appApiName = getAppName(appName);
      if(recordUuid === mainRecordUuid){
        console.log('reload')
        onMerge();
        navigate(`/${appApiName}/${thisObjectApiName}/${mainRecordUuid}`, {replace: true});
      } else {
        console.log('navigate')
        navigate(`/${appApiName}/${thisObjectApiName}/${mainRecordUuid}`);
      }


    } catch (error){
       setErrorMessage("Unhandled error occurred, please contact your system administrator")
       setIsLoading(false)
    }
  }




  interface DependentFieldType {
    Id_sys: string[],
    Uuid_sys: string[],
    CreatedDate_sys: string[],
    IdentifierName_sys: string[]
  }
  const dependentFields: DependentFieldType = {
    Id_sys: ['Uuid_sys', 'CreatedDate_sys', 'IdentifierName_sys'],
    Uuid_sys: ['Id_sys', 'CreatedDate_sys', 'IdentifierName_sys'],
    CreatedDate_sys: ['Id_sys', 'Uuid_sys', 'IdentifierName_sys'],
    IdentifierName_sys: ['Id_sys', 'Uuid_sys', 'CreatedDate_sys']
  }
  const ignoreFields = ['LastModifiedDate_sys',
                        'LastModifiedBy_sys',
                        'SystemModStamp_sys',
                        'Automation_Date_Time_c']

  const selectField = (fieldApiName: string, recordUuid: string) => {
    //do nothing for ignored fields
    if(ignoreFields.includes(fieldApiName)) return

    mergedRecord[fieldApiName] = recordUuid;
    const thisDependentFields = dependentFields[fieldApiName as keyof DependentFieldType] ?? [];
    thisDependentFields.map((thisKey) => mergedRecord[thisKey]=recordUuid);
    setMergedRecord(new MergedRecordModel({...mergedRecord}))
  }

  const selectAll = (recordUuid: string) => {
    const keys = Object.keys(mergedRecord);
    keys.map((thisKey) => {mergedRecord[thisKey]=recordUuid})
    setMergedRecord(new MergedRecordModel({...mergedRecord}))
  }

  const getBackgroundColour = (fieldApiName: string, mergedRecordUuid: string, currentRecordUuid: string) => {
    //selected record/field pairs are light green, otherwise transparent
    if(ignoreFields.includes(fieldApiName)) return 'transparent'
    if(mergedRecordUuid === currentRecordUuid) return 'rgba(0,128,0,0.3)'
    return 'transparent'
  }
  const getTextColour = (fieldApiName: string) => {
    //ignored field values are in grey
    if(ignoreFields.includes(fieldApiName)) return 'grey.500'
    return 'black'
  }


  const cellElement = (field: FieldMetadataModel, record:RecordModel) => {
      const recordFields = record.fields;
      const recordUuidField = recordFields["Uuid_sys"]
      const fieldValue: any = recordFields[field.ApiName_sys].fieldValue
      const fieldType: any = recordFields[field.ApiName_sys].fieldType
      const key = `${field.Uuid_sys} - ${recordUuidField.fieldValue}`

     const backgroundColor = getBackgroundColour(field.ApiName_sys, mergedRecord[field.ApiName_sys], recordUuidField.fieldValue)
     const textColor = getTextColour(field.ApiName_sys)

      if(fieldType === 'ForeignKey'){
        if(fieldValue){
          const lookup_object_name = fieldValue.lookup_object;
          const lookup_uuid = fieldValue.Uuid_sys;
          const thisValue = fieldValue.lookup_display_value
          return (
            <TableCell
               onClick={() => selectField(field.ApiName_sys, recordUuidField.fieldValue)}
               sx={{backgroundColor: backgroundColor,
                    color: textColor}}
               key={key}
               scope="row"
               align={'right'}
            >
                {thisValue}
            </TableCell>
          )
        } else {
          return (
            <TableCell
               onClick={() => selectField(field.ApiName_sys, recordUuidField.fieldValue)}
               sx={{backgroundColor: backgroundColor,
                    color: textColor}}
               key={key}
               scope="row"
               align={'right'}
            >
            </TableCell>
         )
        }
      }

      if(fieldType === 'DateTimeField'){
        const thisValue = formatDatetime(new Date(fieldValue), {variant: 'B'})
        return(
            <TableCell
               onClick={() => selectField(field.ApiName_sys, recordUuidField.fieldValue)}
               sx={{backgroundColor: backgroundColor,
                    color: textColor}}
               key={key}
               scope="row"
               align={'right'}
            >
              {thisValue}
            </TableCell>
        )
      }

      if(fieldType === 'BooleanField'){
        const thisValue = fieldValue === true ? 'x' : ''
        return(
            <TableCell
               onClick={() => selectField(field.ApiName_sys, recordUuidField.fieldValue)}
               sx={{backgroundColor: backgroundColor,
                    color: textColor}}
               key={key}
               scope="row"
               align={'right'}
            >
              {thisValue}
            </TableCell>
        )
      }

      return (
            <TableCell
               onClick={() => selectField(field.ApiName_sys, recordUuidField.fieldValue)}
               sx={{backgroundColor: backgroundColor,
                    color: textColor}}
               key={key}
               scope="row"
               align={'right'}
            >
              {fieldValue}
            </TableCell>
      )


  }


  const rowElement = (field: FieldMetadataModel) => {


    return (
      <TableRow>
        <TableCell
          key={`${field.Uuid_sys}`}
          sx={{color: getTextColour(field.ApiName_sys)}}
        >
          <Typography style={{fontWeight: 'bold'}}>
            {field.Label_sys}
          </Typography>
        </TableCell>

        {duplicateRecords.map((thisRecord) => {
           return cellElement(field, thisRecord)
        })
        }

      </TableRow>

    )
  }



  return (
    <Box sx={{height: '100%'}}>
      <Box
        sx={styles.containerRowD}
      >
        <Button
           style={{ marginLeft: 20, marginTop: 5, marginBottom: 5 }}
           variant="contained"
           color="primary"
           onClick={() => submitMerge()}
           disabled={isLoading}
         >
           Merge Records
         </Button>
       </Box>

      <TableContainer sx={{width: '100%'}}>
         <Table stickyHeader sx={{minWidth: 650}}>
            <TableHead>
               <TableRow>
                  <TableCell
                    key="fieldLabel"
                  >
                    <Typography style={{fontWeight: 'bold'}}>
                       Field Label
                    </Typography>
                  </TableCell>

                  {duplicateRecords.map((record) => {
                         return (
                              <TableCell
                                  key={record.fields['Uuid_sys'].fieldValue}
                                  align={'right'}
                                  onClick={() => selectAll(record.fields['Uuid_sys'].fieldValue)}
                              >
                                <Tooltip
                                  title={
                                    <Typography>
                                       Select this record
                                    </Typography>
                                  }
                                >
                                  <Box sx={styles.containerRowD}>
                                    <Typography style={{fontWeight: 'bold'}}>
                                      Select All
                                    </Typography>
                                    <SelectAllIcon />
                                  </Box>
                                </Tooltip>
                              </TableCell>
                            )
                          })}
                </TableRow>
            </TableHead>

            <TableBody>
               {fields.map((field) => rowElement(field))}
            </TableBody>

            </Table>
        </TableContainer>

      <Box
        sx={styles.containerRowD}
      >
        <Button
           style={{ marginLeft: 20, marginTop: 5, marginBottom: 5 }}
           variant="contained"
           color="primary"
           onClick={() => submitMerge()}
           disabled={isLoading}
         >
           Merge Records
         </Button>
       </Box>

    </Box>
  );

};
